import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import './DataPage.css';
import { formatDate, formatTime } from '../utils/formatDate';

function Financeiro() {
  const [abaAtiva, setAbaAtiva] = useState('dashboard');
  const [resumo, setResumo] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loadingDashboard, setLoadingDashboard] = useState(true);
  const [movimentacoes, setMovimentacoes] = useState([]);
  const [resumoPagamentos, setResumoPagamentos] = useState(null);
  const [locais, setLocais] = useState([]);
  const [formasPagamento, setFormasPagamento] = useState([]);
  const [loading, setLoading] = useState(true);

  // Funções para data padrão (últimos 30 dias)
  const getDataInicial = () => {
    const data = new Date();
    data.setDate(data.getDate() - 30);
    return data.toISOString().split('T')[0];
  };

  const getDataFinal = () => {
    const data = new Date();
    return data.toISOString().split('T')[0];
  };

  // Filtros pagamentos
  const [filtroDataInicio, setFiltroDataInicio] = useState(getDataInicial());
  const [filtroDataFim, setFiltroDataFim] = useState(getDataFinal());
  const [filtroLocal, setFiltroLocal] = useState('');
  const [filtroFormaPgto, setFiltroFormaPgto] = useState('');
  const [ordenacao, setOrdenacao] = useState({ campo: 'DATA', direcao: 'desc' });
  const [agrupamento, setAgrupamento] = useState('nenhum');
  const [gruposExpandidos, setGruposExpandidos] = useState({});

  // Dashboard expandíveis
  const [dashPendentesAberto, setDashPendentesAberto] = useState(false);
  const [dashDivergentesAberto, setDashDivergentesAberto] = useState(false);

  // Aba Pendentes
  const [pendentesLista, setPendentesLista] = useState([]);
  const [loadingPendentes, setLoadingPendentes] = useState(false);
  const [pendentesLocalFilter, setPendentesLocalFilter] = useState('');
  const [pendentesLocais, setPendentesLocais] = useState([]);
  const [pendentesGruposExpandidos, setPendentesGruposExpandidos] = useState({});

  // Modal
  const [modalAberto, setModalAberto] = useState(false);
  const [itemSelecionado, setItemSelecionado] = useState(null);

  useEffect(() => {
    fetchResumo();
  }, []);

  useEffect(() => {
    fetchDashboard();
    fetchLocais();
    setFiltroLocal('');
    setFiltroFormaPgto('');
  }, [filtroDataInicio, filtroDataFim]);

  // Quando local muda, re-busca formas filtradas por local
  useEffect(() => {
    fetchFormasPagamento();
    setFiltroFormaPgto('');
  }, [filtroDataInicio, filtroDataFim, filtroLocal]);

  useEffect(() => {
    if (abaAtiva === 'pagamentos') {
      fetchPagamentos();
    }
  }, [filtroDataInicio, filtroDataFim, filtroLocal, filtroFormaPgto, abaAtiva]);

  useEffect(() => {
    if (abaAtiva === 'pendentes') {
      fetchPendentes();
    }
  }, [abaAtiva, pendentesLocalFilter]);

  const fetchResumo = async () => {
    try {
      const response = await axios.get(`${API_URL}/financeiro/resumo`);
      setResumo(response.data);
    } catch (error) {
      console.error('Erro ao buscar resumo:', error);
    }
  };

  const buildDateParams = () => {
    const params = new URLSearchParams();
    if (filtroDataInicio) params.append('dataInicio', filtroDataInicio);
    if (filtroDataFim) params.append('dataFim', filtroDataFim);
    return params.toString();
  };

  const fetchDashboard = async () => {
    try {
      setLoadingDashboard(true);
      const response = await axios.get(`${API_URL}/financeiro/dashboard?${buildDateParams()}`);
      setDashboard(response.data);
    } catch (error) {
      console.error('Erro ao buscar dashboard:', error);
    } finally {
      setLoadingDashboard(false);
    }
  };

  const fetchLocais = async () => {
    try {
      const response = await axios.get(`${API_URL}/financeiro/locais?${buildDateParams()}`);
      setLocais(response.data);
    } catch (error) {
      console.error('Erro ao buscar locais:', error);
    }
  };

  const fetchFormasPagamento = async () => {
    try {
      const params = new URLSearchParams(buildDateParams());
      if (filtroLocal) params.append('local', filtroLocal);
      const response = await axios.get(`${API_URL}/financeiro/formas-pagamento?${params.toString()}`);
      setFormasPagamento(response.data);
    } catch (error) {
      console.error('Erro ao buscar formas de pagamento:', error);
    }
  };

  const fetchPendentes = async () => {
    try {
      setLoadingPendentes(true);
      const params = new URLSearchParams();
      if (pendentesLocalFilter) params.append('local', pendentesLocalFilter);
      const response = await axios.get(`${API_URL}/financeiro/pendentes?${params.toString()}`);
      const data = response.data;
      setPendentesLista(data);
      // Extrair locais únicos para filtro
      const locaisMap = {};
      data.forEach(r => {
        const id = r.LOCAL || '';
        if (id && !locaisMap[id]) locaisMap[id] = r.LOCAL_NOME || id;
      });
      setPendentesLocais(Object.entries(locaisMap).map(([id, desc]) => ({ ID: id, DESCRICAO: desc })).sort((a, b) => (a.DESCRICAO || '').localeCompare(b.DESCRICAO || '')));
    } catch (error) {
      console.error('Erro ao buscar pendentes:', error);
    } finally {
      setLoadingPendentes(false);
    }
  };

  const fetchPagamentos = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filtroDataInicio) params.append('dataInicio', filtroDataInicio);
      if (filtroDataFim) params.append('dataFim', filtroDataFim);
      if (filtroLocal) params.append('local', filtroLocal);
      if (filtroFormaPgto) params.append('formaPagamento', filtroFormaPgto);

      const response = await axios.get(`${API_URL}/financeiro/movimento-caixa?${params.toString()}`);
      setMovimentacoes(response.data.data);
      setResumoPagamentos(response.data.resumo);
    } catch (error) {
      console.error('Erro ao buscar pagamentos:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const handleOrdenacao = (campo) => {
    setOrdenacao(prev => ({
      campo,
      direcao: prev.campo === campo && prev.direcao === 'asc' ? 'desc' : 'asc'
    }));
  };

  const ordenarDados = (dados) => {
    const dadosOrdenados = [...dados];
    dadosOrdenados.sort((a, b) => {
      const { campo, direcao } = ordenacao;
      let valorA = a[campo];
      let valorB = b[campo];

      if (campo.includes('DATA')) {
        valorA = new Date(valorA || 0).getTime();
        valorB = new Date(valorB || 0).getTime();
      } else if (typeof valorA === 'number') {
        // já é número
      } else {
        valorA = (valorA || '').toString().toLowerCase();
        valorB = (valorB || '').toString().toLowerCase();
      }

      if (direcao === 'asc') {
        return valorA > valorB ? 1 : -1;
      } else {
        return valorA < valorB ? 1 : -1;
      }
    });
    return dadosOrdenados;
  };

  const agruparDados = (dados) => {
    if (agrupamento === 'nenhum') return { 'Todos': dados };

    const grupos = {};
    dados.forEach(item => {
      let chave;
      switch (agrupamento) {
        case 'local':
          chave = item.LOCAL_NOME || item.LOCAL || 'Sem local';
          break;
        case 'convenio':
          chave = item.CONVENIO_NOME || item.CONVENIO || 'Particular';
          break;
        case 'formaPagamento':
          chave = item.FORMA_PAGAMENTO || 'Nao informado';
          break;
        case 'atendente':
          chave = item.USUARIO || 'Nao informado';
          break;
        case 'mes': {
          const data = new Date(item.DATA);
          chave = `${data.getMonth() + 1}/${data.getFullYear()}`;
          break;
        }
        default:
          chave = 'Todos';
      }
      if (!grupos[chave]) grupos[chave] = [];
      grupos[chave].push(item);
    });
    return grupos;
  };

  const renderSetaOrdenacao = (campo) => {
    if (ordenacao.campo !== campo) return ' \u2195';
    return ordenacao.direcao === 'asc' ? ' \u2191' : ' \u2193';
  };

  const toggleGrupo = (nomeGrupo) => {
    setGruposExpandidos(prev => ({
      ...prev,
      [nomeGrupo]: !prev[nomeGrupo]
    }));
  };

  const expandirTodos = () => {
    const ordenados = ordenarDados(movimentacoes);
    const grupos = agruparDados(ordenados);
    const todosGrupos = {};
    Object.keys(grupos).forEach(grupo => {
      todosGrupos[grupo] = true;
    });
    setGruposExpandidos(todosGrupos);
  };

  const colapsarTodos = () => {
    const ordenados = ordenarDados(movimentacoes);
    const grupos = agruparDados(ordenados);
    const todosGrupos = {};
    Object.keys(grupos).forEach(grupo => {
      todosGrupos[grupo] = false;
    });
    setGruposExpandidos(todosGrupos);
  };

  const abrirModal = (item) => {
    setItemSelecionado(item);
    setModalAberto(true);
  };

  const fecharModal = () => {
    setModalAberto(false);
    setItemSelecionado(null);
  };

  const FORMA_COLORS = {
    'Dinheiro': '#10b981',
    'PIX': '#8b5cf6',
    'C. Débito': '#3b82f6',
    'C. Crédito': '#f59e0b',
    'C. Crédito 2x': '#f97316',
    'C. Crédito 3x': '#ef4444',
    'C. Crédito 4x': '#dc2626',
  };

  const getFormaColor = (forma) => FORMA_COLORS[forma] || '#6b7280';

  const styles = {
    pageHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '24px',
      flexWrap: 'wrap',
      gap: '12px',
    },
    pageIcon: {
      fontSize: '1.5rem',
      lineHeight: 1,
    },
    pageTitle: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: '#1a1a2e',
      margin: '0 0 2px 0',
    },
    pageSubtitle: {
      fontSize: '0.85rem',
      color: '#666',
      margin: 0,
    },
  };

  const renderDashboard = () => {
    if (loadingDashboard) {
      return <div style={{ textAlign: 'center', padding: '40px' }}>Carregando dashboard...</div>;
    }
    if (!dashboard) {
      return <div style={{ textAlign: 'center', padding: '40px' }}>Erro ao carregar dashboard</div>;
    }

    const { totais, pendentes, divergentes, porForma, porLocal, listaPendentes, listaDivergentes } = dashboard;
    const totalPago = totais?.TOTAL_PAGO || 0;
    const totalCobrado = totais?.TOTAL_COBRADO || 0;
    const percentualRecebido = totalCobrado > 0 ? ((totalPago / totalCobrado) * 100).toFixed(1) : 0;

    return (
      <div>
        {/* KPIs principais */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          <div style={{ background: '#fff', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderLeft: '4px solid #3b82f6' }}>
            <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: '#6b7280', fontWeight: 600, marginBottom: '8px' }}>Total Cobrado</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1f2937' }}>{formatCurrency(totalCobrado)}</div>
            <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '4px' }}>{totais?.TOTAL_SOLICITACOES || 0} solicitacoes</div>
          </div>
          <div style={{ background: '#fff', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderLeft: '4px solid #10b981' }}>
            <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: '#6b7280', fontWeight: 600, marginBottom: '8px' }}>Total Recebido</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#10b981' }}>{formatCurrency(totalPago)}</div>
            <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '4px' }}>{percentualRecebido}% do cobrado</div>
          </div>
          <div style={{ background: '#fff', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderLeft: '4px solid #ef4444' }}>
            <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: '#6b7280', fontWeight: 600, marginBottom: '8px' }}>Pendentes</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#ef4444' }}>{formatCurrency(pendentes?.VALOR_PENDENTE || 0)}</div>
            <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '4px' }}>{pendentes?.QTD || 0} sem pagamento</div>
          </div>
          <div style={{ background: '#fff', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderLeft: '4px solid #f59e0b' }}>
            <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: '#6b7280', fontWeight: 600, marginBottom: '8px' }}>Divergentes</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#f59e0b' }}>{formatCurrency(divergentes?.VALOR_DIFERENCA || 0)}</div>
            <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '4px' }}>{divergentes?.QTD || 0} com diferenca</div>
          </div>
        </div>

        {/* Barra de progresso cobrado vs recebido */}
        <div style={{ background: '#fff', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '0.85rem' }}>
            <span style={{ fontWeight: 600, color: '#374151' }}>Recebimento no Periodo</span>
            <span style={{ color: '#6b7280' }}>{formatCurrency(totalPago)} / {formatCurrency(totalCobrado)}</span>
          </div>
          <div style={{ height: '12px', backgroundColor: '#e5e7eb', borderRadius: '6px', overflow: 'hidden' }}>
            <div style={{
              height: '100%',
              width: `${Math.min(percentualRecebido, 100)}%`,
              backgroundColor: percentualRecebido >= 90 ? '#10b981' : percentualRecebido >= 70 ? '#f59e0b' : '#ef4444',
              borderRadius: '6px',
              transition: 'width 0.5s ease'
            }} />
          </div>
          <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '4px', textAlign: 'right' }}>
            {percentualRecebido}% recebido
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '20px' }}>
          {/* Por Forma de Pagamento */}
          <div style={{ background: '#fff', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '1rem', fontWeight: 600, color: '#374151' }}>Por Forma de Pagamento</h3>
            {porForma && porForma.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {porForma.map((f) => {
                  const pct = totalPago > 0 ? ((f.TOTAL / totalPago) * 100).toFixed(1) : 0;
                  return (
                    <div key={f.FORMA}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '0.85rem' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ width: '10px', height: '10px', borderRadius: '2px', backgroundColor: getFormaColor(f.FORMA), display: 'inline-block' }} />
                          {f.FORMA}
                        </span>
                        <span style={{ fontWeight: 600 }}>{formatCurrency(f.TOTAL)} <span style={{ fontWeight: 400, color: '#9ca3af' }}>({pct}%)</span></span>
                      </div>
                      <div style={{ height: '6px', backgroundColor: '#f3f4f6', borderRadius: '3px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${pct}%`, backgroundColor: getFormaColor(f.FORMA), borderRadius: '3px' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ color: '#9ca3af', textAlign: 'center', padding: '20px' }}>Nenhum dado</div>
            )}
          </div>

          {/* Por Local */}
          <div style={{ background: '#fff', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '1rem', fontWeight: 600, color: '#374151' }}>Por Unidade</h3>
            {porLocal && porLocal.length > 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, color: '#6b7280' }}>Local</th>
                    <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#6b7280' }}>Atend.</th>
                    <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#6b7280' }}>Valor Pago</th>
                    <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#6b7280' }}>%</th>
                  </tr>
                </thead>
                <tbody>
                  {porLocal.map((l) => {
                    const pct = totalPago > 0 ? ((l.TOTAL_PAGO / totalPago) * 100).toFixed(1) : 0;
                    return (
                      <tr key={l.LOCAL_ID} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '8px', fontWeight: 500 }}>{l.LOCAL_NOME || l.LOCAL_ID}</td>
                        <td style={{ padding: '8px', textAlign: 'right' }}>{l.QTD_SOLICITACOES}</td>
                        <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#10b981' }}>{formatCurrency(l.TOTAL_PAGO)}</td>
                        <td style={{ padding: '8px', textAlign: 'right', color: '#6b7280' }}>{pct}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div style={{ color: '#9ca3af', textAlign: 'center', padding: '20px' }}>Nenhum dado</div>
            )}
          </div>
        </div>

        {/* Pendentes - tabela inline */}
        {listaPendentes && listaPendentes.length > 0 && (
          <div style={{ background: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginTop: '20px', overflow: 'hidden' }}>
            <div
              onClick={() => setDashPendentesAberto(prev => !prev)}
              style={{
                padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                cursor: 'pointer', borderLeft: '4px solid #ef4444', background: dashPendentesAberto ? '#fef2f2' : '#fff',
                transition: 'background 0.2s'
              }}
            >
              <span style={{ fontWeight: 600, color: '#1f2937', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '1.1rem' }}>{dashPendentesAberto ? '\u25BC' : '\u25B6'}</span>
                Sem Pagamento
                <span style={{ background: '#fef2f2', color: '#ef4444', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 700 }}>
                  {pendentes?.QTD || listaPendentes.length}
                </span>
              </span>
              <span style={{ color: '#ef4444', fontWeight: 600, fontSize: '0.9rem' }}>
                {formatCurrency(pendentes?.VALOR_PENDENTE || 0)}
              </span>
            </div>
            {dashPendentesAberto && (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ background: '#fef2f2' }}>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280' }}>Protocolo</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280' }}>Data</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280' }}>Paciente</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280' }}>Local</th>
                      <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#6b7280' }}>Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {listaPendentes.map((item, idx) => (
                      <tr key={item.SOLICITACAO_ID || idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '10px 12px' }}>
                          <a href={`/atendimentos/${item.SOLICITACAO_ID}`} style={{ color: '#2563eb', textDecoration: 'none', fontWeight: 500 }}>
                            {item.LOCAL}-{item.PROTOCOLO}
                          </a>
                        </td>
                        <td style={{ padding: '10px 12px' }}>{formatDate(item.DATA)}</td>
                        <td style={{ padding: '10px 12px' }}>{item.PACIENTE_NOME || '-'}</td>
                        <td style={{ padding: '10px 12px' }}>{item.LOCAL_NOME || item.LOCAL || '-'}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#ef4444' }}>{formatCurrency(item.VALOR_COBRADO)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Divergentes - tabela inline */}
        {listaDivergentes && listaDivergentes.length > 0 && (
          <div style={{ background: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginTop: '16px', overflow: 'hidden' }}>
            <div
              onClick={() => setDashDivergentesAberto(prev => !prev)}
              style={{
                padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                cursor: 'pointer', borderLeft: '4px solid #f59e0b', background: dashDivergentesAberto ? '#fffbeb' : '#fff',
                transition: 'background 0.2s'
              }}
            >
              <span style={{ fontWeight: 600, color: '#1f2937', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '1.1rem' }}>{dashDivergentesAberto ? '\u25BC' : '\u25B6'}</span>
                Pagamento Divergente
                <span style={{ background: '#fffbeb', color: '#d97706', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 700 }}>
                  {divergentes?.QTD || listaDivergentes.length}
                </span>
              </span>
              <span style={{ color: '#d97706', fontWeight: 600, fontSize: '0.9rem' }}>
                {formatCurrency(divergentes?.VALOR_DIFERENCA || 0)} diferenca
              </span>
            </div>
            {dashDivergentesAberto && (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ background: '#fffbeb' }}>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280' }}>Protocolo</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280' }}>Data</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280' }}>Paciente</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280' }}>Local</th>
                      <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#6b7280' }}>Cobrado</th>
                      <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#6b7280' }}>Pago</th>
                      <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#6b7280' }}>Diferenca</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280' }}>Formas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {listaDivergentes.map((item, idx) => {
                      const dif = item.DIFERENCA || 0;
                      return (
                        <tr key={item.SOLICITACAO_ID || idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '10px 12px' }}>
                            <a href={`/atendimentos/${item.SOLICITACAO_ID}`} style={{ color: '#2563eb', textDecoration: 'none', fontWeight: 500 }}>
                              {item.LOCAL}-{item.PROTOCOLO}
                            </a>
                          </td>
                          <td style={{ padding: '10px 12px' }}>{formatDate(item.DATA)}</td>
                          <td style={{ padding: '10px 12px' }}>{item.PACIENTE_NOME || '-'}</td>
                          <td style={{ padding: '10px 12px' }}>{item.LOCAL_NOME || item.LOCAL || '-'}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right' }}>{formatCurrency(item.VALOR_COBRADO)}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#10b981' }}>{formatCurrency(item.VALOR_PAGO)}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: dif > 0 ? '#f59e0b' : '#ef4444' }}>
                            {dif > 0 ? '+' : ''}{formatCurrency(dif)}
                          </td>
                          <td style={{ padding: '10px 12px', fontSize: '0.8rem', color: '#6b7280' }}>{item.FORMAS_PAGAMENTO || '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderPagamentos = () => (
    <>
      {/* Filtros */}
      <div className="filters-container responsive-flex page-filters" style={{ marginBottom: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
        <select
          value={filtroLocal}
          onChange={(e) => setFiltroLocal(e.target.value)}
          style={{ padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
        >
          <option value="">Todos os Locais</option>
          {locais.map((l) => (
            <option key={l.ID} value={l.ID}>{l.DESCRICAO}</option>
          ))}
        </select>
        <select
          value={filtroFormaPgto}
          onChange={(e) => setFiltroFormaPgto(e.target.value)}
          style={{ padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
        >
          <option value="">Todas as Formas</option>
          {formasPagamento.map((f) => (
            <option key={f.FORMA_PAGAMENTO} value={f.FORMA_PAGAMENTO}>{f.FORMA_PAGAMENTO}</option>
          ))}
        </select>
        <button
          onClick={() => {
            setFiltroLocal('');
            setFiltroFormaPgto('');
          }}
          style={{
            padding: '10px 15px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            background: 'white',
            cursor: 'pointer',
            fontSize: '0.85rem'
          }}
        >
          Limpar
        </button>
        <select
          value={agrupamento}
          onChange={(e) => setAgrupamento(e.target.value)}
          style={{ padding: '10px', border: '1px solid #ddd', borderRadius: '4px', background: '#f3f4f6' }}
        >
          <option value="nenhum">Sem agrupamento</option>
          <option value="local">Agrupar por Local</option>
          <option value="convenio">Agrupar por Convenio</option>
          <option value="formaPagamento">Agrupar por Forma Pgto</option>
          <option value="atendente">Agrupar por Atendente</option>
          <option value="mes">Agrupar por Mes</option>
        </select>
      </div>

      {/* Botões de Expandir/Colapsar Grupos */}
      {agrupamento !== 'nenhum' && movimentacoes.length > 0 && (
        <div className="responsive-flex" style={{ marginBottom: '10px', display: 'flex', gap: '10px' }}>
          <button onClick={expandirTodos} style={{ padding: '8px 15px', border: '1px solid #ddd', borderRadius: '4px', background: 'white', cursor: 'pointer', fontSize: '0.85rem' }}>
            Expandir Todos
          </button>
          <button onClick={colapsarTodos} style={{ padding: '8px 15px', border: '1px solid #ddd', borderRadius: '4px', background: 'white', cursor: 'pointer', fontSize: '0.85rem' }}>
            Colapsar Todos
          </button>
        </div>
      )}

      {/* Tabela de Pagamentos */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>Carregando...</div>
      ) : movimentacoes.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>Nenhum pagamento encontrado</div>
      ) : (
        (() => {
          const ordenados = ordenarDados(movimentacoes);
          const grupos = agruparDados(ordenados);

          return Object.entries(grupos).map(([nomeGrupo, itensPorGrupo]) => {
            const totalGrupo = itensPorGrupo.reduce((sum, m) => sum + (m.VALOR || 0), 0);
            const estaExpandido = gruposExpandidos[nomeGrupo] !== false;

            return (
              <div key={nomeGrupo} style={{ marginBottom: '30px' }}>
                {agrupamento !== 'nenhum' && (
                  <div
                    onClick={() => toggleGrupo(nomeGrupo)}
                    style={{
                      background: '#f3f4f6',
                      padding: '10px 15px',
                      borderRadius: estaExpandido ? '8px 8px 0 0' : '8px',
                      fontWeight: 'bold',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '1.2rem' }}>{estaExpandido ? '\u25BC' : '\u25B6'}</span>
                      <span>{nomeGrupo}</span>
                    </span>
                    <span style={{ color: '#10b981' }}>
                      {itensPorGrupo.length} pagamentos - Total: {formatCurrency(totalGrupo)}
                    </span>
                  </div>
                )}
                {estaExpandido && (
                  <>
                  <div className="table-container financeiro-desktop" style={{ overflowX: 'auto' }}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th onClick={() => handleOrdenacao('DATA')} style={{ cursor: 'pointer' }}>
                          Data{renderSetaOrdenacao('DATA')}
                        </th>
                        <th>Hora</th>
                        <th onClick={() => handleOrdenacao('USUARIO')} style={{ cursor: 'pointer' }}>
                          Atendente{renderSetaOrdenacao('USUARIO')}
                        </th>
                        <th onClick={() => handleOrdenacao('PROTOCOLO')} style={{ cursor: 'pointer' }}>
                          Protocolo{renderSetaOrdenacao('PROTOCOLO')}
                        </th>
                        <th onClick={() => handleOrdenacao('PACIENTE_NOME')} style={{ cursor: 'pointer' }}>
                          Paciente{renderSetaOrdenacao('PACIENTE_NOME')}
                        </th>
                        <th>Convenio</th>
                        <th onClick={() => handleOrdenacao('LOCAL')} style={{ cursor: 'pointer' }}>
                          Local{renderSetaOrdenacao('LOCAL')}
                        </th>
                        <th onClick={() => handleOrdenacao('FORMA_PAGAMENTO')} style={{ cursor: 'pointer' }}>
                          Forma Pgto{renderSetaOrdenacao('FORMA_PAGAMENTO')}
                        </th>
                        <th>Arred.</th>
                        <th onClick={() => handleOrdenacao('VALOR')} style={{ cursor: 'pointer' }}>
                          Valor{renderSetaOrdenacao('VALOR')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {itensPorGrupo.map((mov) => (
                        <tr
                          key={mov.ID}
                          onClick={() => abrirModal(mov)}
                          style={{ cursor: 'pointer' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}
                        >
                          <td>{formatDate(mov.DATA)}</td>
                          <td>{formatTime(mov.HORA)}</td>
                          <td>{mov.USUARIO || '-'}</td>
                          <td>
                            <a
                              href={`/atendimentos/${mov.SOLICITACAO_ID}`}
                              onClick={(e) => e.stopPropagation()}
                              style={{ color: '#2563eb', textDecoration: 'none' }}
                            >
                              {mov.LOCAL}-{mov.PROTOCOLO}
                            </a>
                          </td>
                          <td>{mov.PACIENTE_NOME || '-'}</td>
                          <td><small>{mov.CONVENIO_NOME || mov.CONVENIO || 'Particular'}</small></td>
                          <td>{mov.LOCAL_NOME || mov.LOCAL || '-'}</td>
                          <td>{mov.FORMA_PAGAMENTO || '-'}</td>
                          <td>{mov.ARREDONDAMENTO != null && mov.ARREDONDAMENTO !== 0 ? formatCurrency(mov.ARREDONDAMENTO) : '-'}</td>
                          <td style={{ color: '#10b981', fontWeight: 'bold' }}>
                            {formatCurrency(mov.VALOR)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile: Cards Pagamentos */}
                <div className="financeiro-mobile">
                  {itensPorGrupo.map((mov) => (
                    <div
                      key={mov.ID}
                      className="mov-card"
                      onClick={() => abrirModal(mov)}
                    >
                      <div className="mov-card-header">
                        <span>{formatDate(mov.DATA)} {formatTime(mov.HORA)}</span>
                        <span className="mov-card-tipo">{mov.FORMA_PAGAMENTO}</span>
                      </div>
                      <div className="mov-card-desc">
                        {mov.LOCAL}-{mov.PROTOCOLO} | {mov.PACIENTE_NOME || '-'}
                      </div>
                      <div className="mov-card-valor" style={{ color: '#10b981' }}>
                        {formatCurrency(mov.VALOR)}
                      </div>
                    </div>
                  ))}
                </div>
                </>
                )}
              </div>
            );
          });
        })()
      )}

      {/* Total Geral */}
      {!loading && movimentacoes.length > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
          padding: '15px 20px',
          borderRadius: '8px',
          color: 'white',
          fontWeight: 'bold',
          fontSize: '1.1rem',
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: '20px'
        }}>
          <span>Total Geral:</span>
          <span>{movimentacoes.length} pagamentos - {formatCurrency(resumoPagamentos?.total || movimentacoes.reduce((s, m) => s + (m.VALOR || 0), 0))}</span>
        </div>
      )}
    </>
  );

  const agruparPendentesPorMes = (dados) => {
    const grupos = {};
    dados.forEach(item => {
      const d = new Date(item.DATA);
      const chave = `${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
      if (!grupos[chave]) grupos[chave] = [];
      grupos[chave].push(item);
    });
    // Ordenar meses decrescente
    const sorted = Object.entries(grupos).sort(([a], [b]) => {
      const [mA, yA] = a.split('/').map(Number);
      const [mB, yB] = b.split('/').map(Number);
      return yB - yA || mB - mA;
    });
    return sorted;
  };

  const togglePendentesGrupo = (mes) => {
    setPendentesGruposExpandidos(prev => ({ ...prev, [mes]: !prev[mes] }));
  };

  const expandirTodosPendentes = (grupos) => {
    const map = {};
    grupos.forEach(([mes]) => { map[mes] = true; });
    setPendentesGruposExpandidos(map);
  };

  const colapsarTodosPendentes = (grupos) => {
    const map = {};
    grupos.forEach(([mes]) => { map[mes] = false; });
    setPendentesGruposExpandidos(map);
  };

  const renderPendentes = () => {
    const grupos = agruparPendentesPorMes(pendentesLista);
    const totalGeral = pendentesLista.reduce((s, r) => s + (r.VALOR_COBRADO || 0), 0);

    return (
      <>
        {/* Filtro e resumo */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          <select
            value={pendentesLocalFilter}
            onChange={(e) => setPendentesLocalFilter(e.target.value)}
            style={{ padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
          >
            <option value="">Todos os Locais</option>
            {pendentesLocais.map((l) => (
              <option key={l.ID} value={l.ID}>{l.DESCRICAO}</option>
            ))}
          </select>
          {pendentesLista.length > 0 && (
            <>
              <button onClick={() => expandirTodosPendentes(grupos)} style={{ padding: '8px 15px', border: '1px solid #ddd', borderRadius: '4px', background: 'white', cursor: 'pointer', fontSize: '0.85rem' }}>
                Expandir Todos
              </button>
              <button onClick={() => colapsarTodosPendentes(grupos)} style={{ padding: '8px 15px', border: '1px solid #ddd', borderRadius: '4px', background: 'white', cursor: 'pointer', fontSize: '0.85rem' }}>
                Colapsar Todos
              </button>
            </>
          )}
          <div style={{ marginLeft: 'auto', fontSize: '0.9rem', color: '#374151' }}>
            <strong style={{ color: '#ef4444' }}>{pendentesLista.length}</strong> pendentes — Total: <strong style={{ color: '#ef4444' }}>{formatCurrency(totalGeral)}</strong>
          </div>
        </div>

        {loadingPendentes ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>Carregando pendentes...</div>
        ) : pendentesLista.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#10b981', fontWeight: 600 }}>Nenhuma pendencia encontrada</div>
        ) : (
          grupos.map(([mes, itens]) => {
            const totalMes = itens.reduce((s, r) => s + (r.VALOR_COBRADO || 0), 0);
            const expandido = pendentesGruposExpandidos[mes] !== false;

            return (
              <div key={mes} style={{ marginBottom: '12px' }}>
                <div
                  onClick={() => togglePendentesGrupo(mes)}
                  style={{
                    background: '#f3f4f6', padding: '12px 16px',
                    borderRadius: expandido ? '8px 8px 0 0' : '8px',
                    fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    cursor: 'pointer', borderLeft: '4px solid #ef4444', transition: 'all 0.2s'
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '1.1rem' }}>{expandido ? '\u25BC' : '\u25B6'}</span>
                    <span>{mes}</span>
                    <span style={{ background: '#fef2f2', color: '#ef4444', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 700 }}>
                      {itens.length}
                    </span>
                  </span>
                  <span style={{ color: '#ef4444', fontWeight: 600 }}>{formatCurrency(totalMes)}</span>
                </div>
                {expandido && (
                  <>
                    <div className="financeiro-desktop" style={{ overflowX: 'auto' }}>
                      <table className="data-table" style={{ marginBottom: 0 }}>
                        <thead>
                          <tr>
                            <th>Protocolo</th>
                            <th>Data</th>
                            <th>Paciente</th>
                            <th>Convenio</th>
                            <th>Local</th>
                            <th style={{ textAlign: 'right' }}>Valor</th>
                          </tr>
                        </thead>
                        <tbody>
                          {itens.map((item) => (
                            <tr key={item.SOLICITACAO_ID}>
                              <td>
                                <a href={`/atendimentos/${item.SOLICITACAO_ID}`} style={{ color: '#2563eb', textDecoration: 'none', fontWeight: 500 }}>
                                  {item.LOCAL}-{item.PROTOCOLO}
                                </a>
                              </td>
                              <td>{formatDate(item.DATA)}</td>
                              <td>{item.PACIENTE_NOME || '-'}</td>
                              <td><small>{item.CONVENIO_NOME || item.CONVENIO || '-'}</small></td>
                              <td>{item.LOCAL_NOME || item.LOCAL || '-'}</td>
                              <td style={{ textAlign: 'right', fontWeight: 600, color: '#ef4444' }}>{formatCurrency(item.VALOR_COBRADO)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="financeiro-mobile">
                      {itens.map((item) => (
                        <div key={item.SOLICITACAO_ID} className="mov-card">
                          <div className="mov-card-header">
                            <a href={`/atendimentos/${item.SOLICITACAO_ID}`} style={{ color: '#2563eb', textDecoration: 'none', fontWeight: 500 }}>
                              {item.LOCAL}-{item.PROTOCOLO}
                            </a>
                            <span>{formatDate(item.DATA)}</span>
                          </div>
                          <div className="mov-card-desc">{item.PACIENTE_NOME || '-'} | {item.LOCAL_NOME || '-'}</div>
                          <div className="mov-card-valor" style={{ color: '#ef4444' }}>{formatCurrency(item.VALOR_COBRADO)}</div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            );
          })
        )}

        {/* Total Geral */}
        {!loadingPendentes && pendentesLista.length > 0 && (
          <div style={{
            background: 'linear-gradient(135deg, #ef4444 0%, #f97316 100%)',
            padding: '15px 20px', borderRadius: '8px', color: 'white',
            fontWeight: 'bold', fontSize: '1.1rem', display: 'flex',
            justifyContent: 'space-between', marginTop: '20px'
          }}>
            <span>Total Pendente:</span>
            <span>{pendentesLista.length} solicitacoes - {formatCurrency(totalGeral)}</span>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="page-container data-page">
      <div style={styles.pageHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={styles.pageIcon}>💰</span>
          <div>
            <h1 style={styles.pageTitle}>Financeiro</h1>
            <p style={styles.pageSubtitle}>Dashboard e pagamentos do laboratorio</p>
          </div>
        </div>
      </div>

      {/* Filtros de Data (compartilhados) */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="date"
          value={filtroDataInicio}
          onChange={(e) => setFiltroDataInicio(e.target.value)}
          style={{ padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
        />
        <input
          type="date"
          value={filtroDataFim}
          onChange={(e) => setFiltroDataFim(e.target.value)}
          style={{ padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
        />
        <button
          onClick={() => {
            setFiltroDataInicio(getDataInicial());
            setFiltroDataFim(getDataFinal());
          }}
          style={{
            padding: '10px 15px', border: '1px solid #ddd', borderRadius: '4px',
            background: 'white', cursor: 'pointer', fontSize: '0.85rem'
          }}
        >
          Ultimos 30 dias
        </button>
        {resumo && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '16px', fontSize: '0.85rem', color: '#374151' }}>
            <span>Hoje: <strong style={{ color: '#10b981' }}>{formatCurrency(resumo.ENTRADA_CAIXA_HOJE)}</strong></span>
          </div>
        )}
      </div>

      {/* Abas */}
      <div style={{
        display: 'flex', borderBottom: '2px solid #e5e7eb', marginBottom: '20px',
        background: '#fff', borderRadius: '8px 8px 0 0', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        {[
          { id: 'dashboard', label: 'Dashboard' },
          { id: 'pagamentos', label: 'Pagamentos' },
          { id: 'pendentes', label: `Pendentes${dashboard?.pendentes?.QTD ? ` (${dashboard.pendentes.QTD})` : ''}` }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setAbaAtiva(tab.id)}
            style={{
              padding: '14px 24px', fontSize: '14px', fontWeight: abaAtiva === tab.id ? 600 : 500,
              cursor: 'pointer', border: 'none', background: 'none',
              color: abaAtiva === tab.id ? '#1976d2' : '#666',
              borderBottom: abaAtiva === tab.id ? '3px solid #1976d2' : '3px solid transparent',
              transition: 'all 0.2s', outline: 'none'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {abaAtiva === 'dashboard' && renderDashboard()}
      {abaAtiva === 'pagamentos' && renderPagamentos()}
      {abaAtiva === 'pendentes' && renderPendentes()}

      {/* Modal de Detalhes */}
      {modalAberto && itemSelecionado && (
        <div className="modal-overlay" onClick={fecharModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Detalhes do Pagamento</h2>
              <button className="modal-close" onClick={fecharModal}>&#x2715;</button>
            </div>
            <div className="modal-body">
              <div className="detalhes-grid">
                {Object.entries(itemSelecionado).map(([key, value]) => (
                  <div key={key} className="detalhe-item">
                    <strong>{key}:</strong>
                    <span>
                      {key.includes('DATA') && value ? formatDate(value) :
                       key.includes('VALOR') || key === 'ARREDONDAMENTO' ? formatCurrency(value) :
                       value !== null && value !== undefined ? String(value) : '-'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }
        .modal-content {
          background: white;
          border-radius: 12px;
          max-width: 800px;
          width: 90%;
          max-height: 80vh;
          overflow-y: auto;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        }
        .modal-header {
          padding: 20px;
          border-bottom: 1px solid #e5e7eb;
          display: flex;
          justify-content: space-between;
          align-items: center;
          position: sticky;
          top: 0;
          background: white;
          z-index: 10;
        }
        .modal-header h2 {
          margin: 0;
          font-size: 1.5rem;
          color: #1f2937;
        }
        .modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #6b7280;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
          transition: all 0.2s;
        }
        .modal-close:hover {
          background-color: #f3f4f6;
          color: #1f2937;
        }
        .modal-body {
          padding: 20px;
        }
        .detalhes-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 15px;
        }
        .detalhe-item {
          display: flex;
          flex-direction: column;
          gap: 5px;
          padding: 12px;
          background: #f9fafb;
          border-radius: 6px;
          border-left: 3px solid #2563eb;
        }
        .detalhe-item strong {
          font-size: 0.75rem;
          text-transform: uppercase;
          color: #6b7280;
          letter-spacing: 0.05em;
        }
        .detalhe-item span {
          font-size: 0.95rem;
          color: #1f2937;
          word-break: break-word;
        }
      `}</style>
    </div>
  );
}

export default Financeiro;
